import Big from 'big.js';
import { forwardRef } from 'react';
import { container, symbol } from './style.css';
import { Box, BoxProps } from '@ff-it/ui';
import clsx from 'clsx';
const intFormatter = new Intl.NumberFormat('en-US');

function needsApprox(value: string | Big): boolean {
  // Ultra-fast O(1) mathematical path for Big.js instances
  // Big.js drops trailing zeros. Decimal places = c.length - e - 1
  if (typeof value !== 'string') {
    return value.c.length - value.e - 1 > 2;
  }

  // Fast path for strings (No memory allocation: avoids .slice and .repeat)
  const dot = value.indexOf('.');
  if (dot === -1) return false;

  // Check for any non-zero digit after 2 decimal places
  for (let i = dot + 3; i < value.length; i++) {
    if (value[i] !== '0') return true;
  }

  return false;
}

function formatMoney(value: string | Big, exact?: boolean): { display: string; approx: boolean } {
  const approx = exact ? false : needsApprox(value);

  const rounded = approx
    ? typeof value === 'string'
      ? new Big(value).toFixed(2)
      : value.toFixed(2)
    : typeof value === 'string'
      ? value
      : value.toFixed();

  // Fast extraction without array allocations (avoids .split)
  let intPart: string;
  let fracPart: string;
  const dotIndex = rounded.indexOf('.');

  if (dotIndex === -1) {
    intPart = rounded;
    fracPart = '00';
  } else {
    intPart = rounded.slice(0, dotIndex);
    fracPart = rounded.slice(dotIndex + 1, dotIndex + 3).padEnd(2, '0');
  }

  const formattedInt = intFormatter.format(Number(intPart));
  const display = `${formattedInt}.${fracPart}`;

  return { display, approx };
}

export type MoneyCellProps = {
  value: string | Big | null;
  exact?: boolean;
  percent?: boolean;
  testId?: string;
} & Omit<BoxProps, 'title' | 'value'>;

export const MoneyCell = forwardRef<HTMLDivElement, MoneyCellProps>(function MoneyCell(
  { value, exact, percent, className, children, ...rest },
  ref,
) {
  if (value === null) {
    return null;
  }
  const { display, approx } = formatMoney(value, exact);

  const strValue = typeof value === 'string' ? value : value.toFixed();
  const title = approx ? (percent ? `${strValue}%` : strValue) : undefined;
  return (
    <Box ref={ref} className={clsx(container, className)} title={title} data-value={strValue} {...rest}>
      {approx && <span className={symbol}>≈</span>}
      {display}
      {children}
      {percent && <span className={symbol}>%</span>}
    </Box>
  );
});
